/**
 * AdminCourseTable
 *
 * Admin-only Course list view. Replaces the learner-facing card grid with a
 * data table tuned for admin workflows: create / edit / assign / delete,
 * with bulk publish / unpublish / delete on selected rows.
 *
 * Built on the shared `PagedTable` + `usePagedList` primitives so it matches
 * Students, Workspaces, and Groups in look and behavior.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { Course, CourseType } from '../../apis/api-client';
import { coursesClient } from '../../apis/client-instances';
import { removeCourse, updateCourse } from '../../features/courses/coursesSlice';
import { alert, getFileUrl } from '../../hooks/function';
import { usePagedList } from '../../hooks/usePagedList';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '../ui';
import {
  BarChartIcon,
  ContentCopyIcon,
  DeleteIcon,
  EditIcon,
  MoreVertIcon,
  OpenInNewIcon,
  PersonAddIcon,
  VisibilityIcon,
  VisibilityOffIcon,
} from '../icons';
import PagedTable, { PagedTableColumn, SortDirection } from '../common/PagedTable';
import ConfirmDialog from '../ConfirmDialog';
import EditAssignmentDialog from '../assignments/EditAssignmentDialog';
import DuplicateCourseDialog from './DuplicateCourseDialog';
import { selectAuthorEntities, useEnsureAuthors } from '../../features/lookups/lookupsSlice';
import { RootState } from '../../types';
import { UserRole } from '../../apis/api-client';
import { isContentAuthorRole } from '../../utils/roleHelpers';

interface AdminCourseTableProps {
  /** Filter state from the toolbar (tags, dates, workspace, draft). */
  filters: any;
}

const AdminCourseTable: React.FC<AdminCourseTableProps> = ({ filters }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  // Load the shared author directory so instructor names resolve in rows.
  useEnsureAuthors();
  const authorsById = useSelector(selectAuthorEntities);
  // Workspace admins are scoped to "manage assignments + view reports"
  // only — no editing or deleting courses (those flows live with
  // account/system admins who own the content roster). Drives:
  //   - row click → preview (vs. edit for higher roles)
  //   - Edit / Delete actions suppressed everywhere in this table
  const role = useSelector((state: RootState) => state.profile.user?.role);
  const isWorkspaceAdmin = role === UserRole.WorkspaceAdmin;
  // See `isContentAuthorRole` for the Super-Admin-only target + why
  // `AccountAdmin` is still admitted.
  const canAuthorContent = isContentAuthorRole(role);

  // ── Per-row dialog state ──────────────────────────────────────────────
  const [assignTarget, setAssignTarget] = useState<Course | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Course | null>(null);
  const [duplicateTarget, setDuplicateTarget] = useState<Course | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);

  // Navigate to the full-page editor for a given course slug.
  // useCallback keeps the columns useMemo stable across renders.
  const goToEdit = useCallback(
    (c: Course) => {
      const slug = (c as any).slug || c.id;
      if (slug != null) navigate(`/courses/${slug}/edit`);
    },
    [navigate]
  );

  // Learner-facing preview — the same surface the catalog opens. Used
  // both for the workspace-admin row click (no edit access) and the
  // "Preview" item in the actions dropdown for higher roles.
  const goToPreview = useCallback(
    (c: Course) => {
      const slug = (c as any).slug || c.id;
      if (slug != null) navigate(`/courses/${slug}`);
    },
    [navigate]
  );

  // Course-scoped reports. The Reports page's Activity tab accepts a
  // `courseIds` URL parameter and already honors the global workspace
  // switcher, so a single navigate with both params lands the admin
  // on the right report filtered to this course within this workspace.
  const goToReports = useCallback(
    (c: Course) => {
      if (c.id == null) return;
      navigate(`/reports?tab=activity&courseIds=${c.id}`);
    },
    [navigate]
  );

  // ── Row action menu (More ⋮) ──────────────────────────────────────────
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<Course | null>(null);
  const openMenu = (e: React.MouseEvent<HTMLElement>, row: Course) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  // ── Selection ─────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);

  // ── Sort ──────────────────────────────────────────────────────────────
  // Default to most-recently-updated first — that's the question admins ask
  // most often when scanning the catalog ("what changed recently?").
  // NOTE: The backend's `getCourses` does not yet accept sort params, so this
  // state is wired through the UI but not yet sent to the API. Header arrows
  // update on click; the actual reorder will activate once the API adds
  // `sortBy` / `sortDir` params and we plumb them into the fetcher below.
  const [sortBy, setSortBy] = useState<string>('updatedAt');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');

  // ── Paged data ────────────────────────────────────────────────────────
  // Admin view shows all content types by default — Courses, Events, and
  // Learning Paths together — distinguished by the Type column. The toolbar
  // can narrow to a single type via filters.courseType. Search lives in the
  // toolbar as filters.name (debounced there) so the table itself doesn't
  // render a second search input.
  const fetcher = useCallback(
    async ({ page, pageSize }: { page: number; pageSize: number; search: string }) => {
      // The API accepts a SINGLE courseType. For the multi-select filter we
      // distinguish three cases:
      //   • 0 types  → no filter (undefined)
      //   • 1 type   → pass directly to the API (server-side filter)
      //   • 2 types  → server can't express "either A or B" + paging
      //                together, so we fall back to the FULL_LIMIT client-side
      //                path (same one used by title sort) and filter locally.
      //   • all 3    → equivalent to no filter
      const selectedTypes: CourseType[] = filters.courseType ?? [];
      const TOTAL_TYPE_COUNT = 3;
      const isAllOrNoneTypes = selectedTypes.length === 0 || selectedTypes.length === TOTAL_TYPE_COUNT;
      const singleType = selectedTypes.length === 1 ? selectedTypes[0] : undefined;
      const needsClientTypeFilter = !isAllOrNoneTypes && selectedTypes.length !== 1;

      // Workspace admins only see PUBLISHED content — drafts are an
      // authoring concern owned by account/system admins. The Status
      // filter chip is hidden for them too (see AdminCourseFilters),
      // but we pin `isDraft=false` server-side here as the
      // authoritative gate.
      const effectiveIsDraft = isWorkspaceAdmin ? false : filters.isDraft;

      const args = [
        undefined, // id
        filters.name || undefined,
        undefined, // accountId
        filters.workspaceId,
        effectiveIsDraft,
        filters.tagIds,
        filters.publishedDateFrom ? new Date(filters.publishedDateFrom) : undefined,
        filters.publishedDateTo ? new Date(filters.publishedDateTo) : undefined,
        undefined, // myCourses
        filters.isAssigned,
        // For multi-type and "all"/"none", the server param is undefined and
        // any narrowing happens locally below.
        singleType,
      ] as const;

      // Client-side fallback path: used when title sort is active OR when
      // multiple (but not all) types are selected. Fetches a broad window
      // and applies sort/type-filter/pagination locally.
      // TODO: once the API accepts sortBy/sortDir AND multi-value courseType,
      // collapse this branch into the default path.
      const FULL_LIMIT = 1000;
      const needsClientPath = sortBy === 'title' || needsClientTypeFilter;

      if (needsClientPath) {
        const allItems = await coursesClient.getCourses(
          args[0], args[1], args[2], args[3], args[4], args[5], args[6], args[7],
          args[8], args[9], args[10],
          0,
          FULL_LIMIT
        );
        let filtered = needsClientTypeFilter
          ? allItems.filter((c: Course) => c.courseType != null && selectedTypes.includes(c.courseType))
          : allItems;
        if (sortBy === 'title') {
          filtered = [...filtered].sort((a, b) => {
            // case-insensitive + numeric — keeps "Course 2" before "Course 10".
            const cmp = (a.title || '').localeCompare(b.title || '', undefined, {
              sensitivity: 'base',
              numeric: true,
            });
            return sortDir === 'asc' ? cmp : -cmp;
          });
        }
        // When type-filtering client-side, the server count is wrong (it
        // counts the unfiltered set). Compute from the filtered result.
        const totalCount = needsClientTypeFilter
          ? filtered.length
          : await coursesClient.getCoursesCount(
              args[0], args[1], args[2], args[3], args[4], args[5], args[6], args[7],
              args[8], args[9],
              undefined
            );
        const start = (page - 1) * pageSize;
        const slice = filtered.slice(start, start + pageSize);
        const withThumbs = await Promise.all(
          slice.map(async (c: Course) => ({
            ...c,
            thumbnailUrl: c.thumbnail ? await getFileUrl(c.thumbnail) : null,
          }))
        );
        return { items: withThumbs, totalCount, page, pageSize };
      }

      // Default: server-paged fetch. Other sortable columns currently use the
      // server's default order until the backend wires up sort.
      const offset = (page - 1) * pageSize;
      const [list, totalCount] = await Promise.all([
        coursesClient.getCourses(
          args[0], args[1], args[2], args[3], args[4], args[5], args[6], args[7],
          args[8], args[9], args[10],
          offset,
          pageSize
        ),
        coursesClient.getCoursesCount(
          args[0], args[1], args[2], args[3], args[4], args[5], args[6], args[7],
          args[8], args[9],
          args[10] // mirror the courseType param so the count matches what we display
        ),
      ]);

      // Hydrate thumbnails to SAS-signed URLs so the table can show them.
      const withThumbs = await Promise.all(
        list.map(async (c: Course) => ({
          ...c,
          thumbnailUrl: c.thumbnail ? await getFileUrl(c.thumbnail) : null,
        }))
      );

      return {
        items: withThumbs,
        totalCount,
        page,
        pageSize,
      };
    },
    // sortBy/sortDir included so the fetcher reruns when the user clicks a
    // sortable header. Title sort is honored client-side here; other
    // columns will activate once the backend wires up sortBy/sortDir.
    // `isWorkspaceAdmin` flips the published-only gate above.
    [filters, sortBy, sortDir, isWorkspaceAdmin]
  );

  const paged = usePagedList<Course & { thumbnailUrl?: string | null }>({
    fetcher,
    initialPageSize: 25,
    onError: () => alert(enqueueSnackbar, 'Failed to load courses', 'error'),
    deps: [filters, sortBy, sortDir, isWorkspaceAdmin],
  });

  // ── Delete handlers ────────────────────────────────────────────────────
  // Save lives on the EditCoursePage now — the table doesn't host a dialog.
  const handleDeleteConfirmed = async () => {
    if (!deleteTarget?.id) return;
    try {
      await coursesClient.deleteCourse(deleteTarget.id);
      dispatch(removeCourse(deleteTarget.id));
      alert(enqueueSnackbar, 'Course deleted', 'success');
      paged.refresh();
    } catch {
      alert(enqueueSnackbar, 'Failed to delete course', 'error');
    } finally {
      setDeleteTarget(null);
    }
  };

  // Bulk operations — fired sequentially against the singular endpoints.
  // The backend has no batch routes (out of scope to add), so we loop and
  // report aggregate success/failure to the user.
  const runBulk = async (op: 'publish' | 'unpublish' | 'delete') => {
    const ids = [...selectedIds].map((x) => Number(x));
    if (ids.length === 0) return;

    let okCount = 0;
    let failCount = 0;
    for (const id of ids) {
      try {
        if (op === 'delete') {
          await coursesClient.deleteCourse(id);
          dispatch(removeCourse(id));
        } else {
          // Fetch the course so we update with the full payload — partial
          // updates aren't safe with the current update endpoint.
          const existing = await coursesClient.getCourseById(id);
          const next = { ...existing, isDraft: op === 'unpublish' };
          await coursesClient.updateCourse(id, next as any);
          dispatch(updateCourse({ id, course: next as any }));
        }
        okCount += 1;
      } catch {
        failCount += 1;
      }
    }

    const verb = op === 'delete' ? 'deleted' : op === 'publish' ? 'published' : 'unpublished';
    if (failCount === 0) {
      alert(enqueueSnackbar, `${okCount} ${okCount === 1 ? 'course' : 'courses'} ${verb}`, 'success');
    } else {
      alert(
        enqueueSnackbar,
        `${verb} ${okCount}; failed ${failCount}`,
        okCount > 0 ? 'warning' : 'error'
      );
    }
    setSelectedIds([]);
    setBulkDeleteOpen(false);
    paged.refresh();
  };

  // ── Column definitions ────────────────────────────────────────────────
  const columns: PagedTableColumn<Course & { thumbnailUrl?: string | null }>[] = useMemo(
    () => [
      {
        key: 'title',
        label: 'Content Title',
        sortable: true,
        render: (c) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: 80,
                height: 45,
                borderRadius: 1,
                bgcolor: 'grey.100',
                flexShrink: 0,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {c.thumbnailUrl ? (
                <Box
                  component="img"
                  src={c.thumbnailUrl}
                  alt=""
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : null}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={600} noWrap title={c.title || ''}>
                {c.title}
              </Typography>
              {(() => {
                // Resolve the instructor from the shared author directory.
                const author = c.authorId != null ? authorsById[c.authorId] : undefined;
                if (!author?.name) return null;
                return (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    title={author.name}
                  >
                    {author.name}
                  </Typography>
                );
              })()}
            </Box>
          </Box>
        ),
      },
      {
        key: 'type',
        label: 'Type',
        width: 140,
        sortable: true,
        render: (c) => {
          // Pastel palette across all three so they read as quiet labels,
          // not buttons. Event uses soft blue, Learning Path uses soft
          // purple, plain Course stays the default neutral grey.
          if (c.courseType === CourseType.LearningPath) {
            return (
              <Chip
                label="Learning Path"
                size="small"
                sx={{ bgcolor: '#f3e5f5', color: '#4a148c', fontWeight: 600 }}
              />
            );
          }
          if (c.courseType === CourseType.External) {
            return (
              <Chip
                label="Event"
                size="small"
                icon={<OpenInNewIcon sx={{ fontSize: 14, color: '#0d47a1 !important' }} />}
                sx={{ bgcolor: '#e3f2fd', color: '#0d47a1', fontWeight: 600 }}
              />
            );
          }
          return <Chip label="Course" size="small" sx={{ fontWeight: 600 }} />;
        },
      },
      {
        key: 'status',
        label: 'Status',
        width: 120,
        sortable: true,
        render: (c) =>
          c.isDraft ? (
            <Chip label="Draft" size="small" color="default" sx={{ fontWeight: 600 }} />
          ) : (
            <Chip
              label="Published"
              size="small"
              // Flat pastel green — soft tint with darker text for readability.
              sx={{
                fontWeight: 600,
                bgcolor: 'success.softBg',
                color: 'success.dark',
              }}
            />
          ),
      },
      // Tags column — account/system admins only. Workspace admins
      // can't filter or manage tags, so the column would be read-only
      // noise on their roster.
      ...(isWorkspaceAdmin
        ? []
        : [{
            key: 'tags',
            label: 'Tags',
            render: (c: Course & { thumbnailUrl?: string | null }) =>
              c.tags && c.tags.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {c.tags.slice(0, 3).map((t) => (
                    <Chip key={t.id} label={t.name} size="small" variant="outlined" />
                  ))}
                  {c.tags.length > 3 && (
                    <Chip label={`+${c.tags.length - 3}`} size="small" variant="outlined" />
                  )}
                </Box>
              ) : (
                <Typography variant="caption" color="text.disabled">—</Typography>
              ),
          } satisfies PagedTableColumn<Course & { thumbnailUrl?: string | null }>]),
      {
        key: 'updatedAt',
        label: 'Updated',
        width: 140,
        sortable: true,
        render: (c) => {
          const d = c.updatedAt || c.createdAt;
          return d ? (
            <Typography variant="body2" color="text.secondary">
              {new Date(d).toLocaleDateString()}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.disabled">—</Typography>
          );
        },
      },
      {
        key: 'actions',
        label: 'Actions',
        align: 'right',
        width: 80,
        // Single kebab affordance — keeps the row scannable instead of
        // three competing icon buttons. The dropdown carries Preview /
        // Assign / Reports (and, for higher roles only, Edit + Delete).
        render: (c) => (
          <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex' }}>
            <Tooltip title="Actions">
              <IconButton size="small" onClick={(e) => openMenu(e, c)}>
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [goToEdit, isWorkspaceAdmin, authorsById]
  );

  const hasSelection = selectedIds.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PagedTable<Course & { thumbnailUrl?: string | null }>
        stickyHeader
        columns={columns}
        rows={paged.items}
        totalCount={paged.totalCount}
        page={paged.page}
        pageSize={paged.pageSize}
        loading={paged.loading}
        rowKey={(c) => c.id ?? c.slug ?? ''}
        onPageChange={paged.setPage}
        onPageSizeChange={paged.setPageSize}
        onRowClick={(c) => (canAuthorContent ? goToEdit(c) : goToPreview(c))}
        emptyMessage="No content found"
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={(key, dir) => {
          setSortBy(key);
          setSortDir(dir);
        }}
      />

      {/* Row-level actions menu — single source of per-row actions
          across roles. Workspace admins see Preview / Assign / Reports
          only. Account/system admins additionally get Edit + Delete
          since they own the content roster. */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            if (menuRow) goToPreview(menuRow);
            closeMenu();
          }}
        >
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} /> Preview
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuRow) setAssignTarget(menuRow);
            closeMenu();
          }}
        >
          <PersonAddIcon fontSize="small" sx={{ mr: 1 }} /> Assign
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuRow) goToReports(menuRow);
            closeMenu();
          }}
        >
          <BarChartIcon fontSize="small" sx={{ mr: 1 }} /> Reports
        </MenuItem>
        {canAuthorContent && (
          <MenuItem
            onClick={() => {
              if (menuRow) goToEdit(menuRow);
              closeMenu();
            }}
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
          </MenuItem>
        )}
        {canAuthorContent && (
          <MenuItem
            onClick={() => {
              if (menuRow) setDuplicateTarget(menuRow);
              closeMenu();
            }}
          >
            <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} /> Duplicate
          </MenuItem>
        )}
        {canAuthorContent && (
          <MenuItem
            onClick={() => {
              if (menuRow) setDeleteTarget(menuRow);
              closeMenu();
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
          </MenuItem>
        )}
      </Menu>

      {/* Single-row assignment dialog with course pre-selected. */}
      {assignTarget && (
        <EditAssignmentDialog
          open={!!assignTarget}
          courseId={assignTarget.id}
          onClose={() => setAssignTarget(null)}
          onSuccess={() => alert(enqueueSnackbar, 'Assignment created', 'success')}
        />
      )}

      {/* Bulk assignment dialog — same form, looped server-side once per
          selected courseId. Clears selection on success. */}
      {bulkAssignOpen && (
        <EditAssignmentDialog
          open={bulkAssignOpen}
          courseIds={selectedIds.map((id) => Number(id)).filter((n) => Number.isFinite(n))}
          onClose={() => setBulkAssignOpen(false)}
          onSuccess={() => {
            setBulkAssignOpen(false);
            setSelectedIds([]);
          }}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Course"
        content={`Are you sure you want to delete "${deleteTarget?.title}"? This cannot be undone.`}
        // Typed-confirm safeguard — deleting a course cascades to its
        // lessons, assignments, and learner enrollments. A misclicked
        // "Yes" on a generic dialog was too easy; requiring "DELETE"
        // forces an intentional act.
        confirmText="DELETE"
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Duplicate flow — prompts for a new title (defaulted to
          "<source> copy"), then clones the course + its lessons.
          Mounted unconditionally so the dialog can close-animate; it
          short-circuits on `open=false`. */}
      <DuplicateCourseDialog
        open={!!duplicateTarget}
        source={duplicateTarget}
        onClose={() => setDuplicateTarget(null)}
        onDuplicated={() => {
          // Refresh the current page so the new draft shows up at the
          // top of "recent" / picked up by the active filters.
          paged.refresh();
        }}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        title="Delete Selected"
        content={`Delete ${selectedIds.length} ${selectedIds.length === 1 ? 'course' : 'courses'}? This cannot be undone.`}
        // Same typed-confirm safeguard as the single-row delete. The
        // bulk path is even higher stakes — a misclick wipes every
        // selected course at once — so it gets the same treatment.
        confirmText="DELETE"
        onConfirm={() => runBulk('delete')}
        onCancel={() => setBulkDeleteOpen(false)}
      />

      {/* Floating bulk-action bar. position: fixed so it overlays the bottom
          of the viewport instead of pushing the table down. Always mounted
          so the slide-in/out can animate; pointer events are disabled when
          empty so the bar can't be clicked while hidden. */}
      <Box
        sx={{
          position: 'fixed',
          left: '50%',
          bottom: 24,
          transform: hasSelection
            ? 'translate(-50%, 0)'
            : 'translate(-50%, calc(100% + 32px))',
          opacity: hasSelection ? 1 : 0,
          pointerEvents: hasSelection ? 'auto' : 'none',
          transition:
            'transform 280ms cubic-bezier(0.4, 0, 0.2, 1), opacity 220ms ease',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2.5,
          py: 1.25,
          minWidth: 480,
          borderRadius: 999,
          bgcolor: '#1f2937',
          color: 'white',
          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.35), 0 4px 10px rgba(0, 0, 0, 0.2)',
          zIndex: (theme) => theme.zIndex.snackbar,
        }}
      >
        <Typography fontWeight={600} sx={{ mr: 1.5 }}>
          {selectedIds.length} selected
        </Typography>

        <Box sx={{ flex: 1 }} />

        <Button
          size="small"
          startIcon={<PersonAddIcon />}
          onClick={() => setBulkAssignOpen(true)}
          sx={{
            color: '#bae6fd',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
          }}
        >
          Assign
        </Button>
        {/* Publish / Unpublish / Delete are authoring operations on
            the catalog itself — Super Admin only. Workspace + Group
            admins still see the Assign button so they can hand out
            existing content. */}
        {canAuthorContent && (
          <>
            <Button
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => runBulk('publish')}
              sx={{
                color: '#a7f3d0',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
              }}
            >
              Publish
            </Button>
            <Button
              size="small"
              startIcon={<VisibilityOffIcon />}
              onClick={() => runBulk('unpublish')}
              sx={{
                color: 'rgba(255,255,255,0.85)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
              }}
            >
              Unpublish
            </Button>
            <Button
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => setBulkDeleteOpen(true)}
              sx={{
                color: '#fca5a5',
                '&:hover': { bgcolor: 'rgba(252, 165, 165, 0.12)' },
              }}
            >
              Delete
            </Button>
          </>
        )}
        <Box
          sx={{
            width: '1px',
            height: 24,
            bgcolor: 'rgba(255,255,255,0.15)',
            mx: 0.5,
          }}
        />
        <Button
          size="small"
          onClick={() => setSelectedIds([])}
          sx={{
            color: 'rgba(255,255,255,0.6)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
          }}
        >
          Clear
        </Button>
      </Box>
    </Box>
  );
};

export default AdminCourseTable;
